import React, { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router'
import { ROUTES } from '@constants/index'
import { usePathBuilder } from '@hooks/usePathBuilder'
import { useAppNavigate } from '@hooks/useAppNavigate'
import { CustomerFacingClaimRecordStatus } from '@typesApp/ClaimRecord'
import { useLazyGetClaimRecordQuery } from '@redux/queries/claimRecords'
import { resetLastForm } from '@redux/slices/formsSlice'
import { useErrorPage } from '@pages/Error/hooks/useErrorPage'
import { getCustomerFacingStatus } from '@utils/claimUtil'
import { getDefaultView } from '@utils/defaultView'
import { getResumeToView } from '@utils/resumeToView'
import { clearLocalFormState, getLocalFormState } from '@utils/localFormState'
import { LoadingSkeleton } from './LoadingSkeleton/LoadingSkeleton'

type ResumeState = {
  claimRecordId?: string
}

export const ResumeRedirect: React.FC = () => {
  const buildPath = usePathBuilder()
  const navigate = useAppNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const { showErrorPage } = useErrorPage()
  const [fetchClaimRecord] = useLazyGetClaimRecordQuery()

  const state: ResumeState = location.state

  useEffect(() => {
    if (!state?.claimRecordId) return

    fetchClaimRecord(state.claimRecordId)
      .unwrap()
      .then(claimRecord => {
        // restore to last dynamic form
        const formState = getLocalFormState()
        dispatch(resetLastForm(formState?.formIndex))

        // resume to view configured in pageflow
        const status = getCustomerFacingStatus(claimRecord)

        if (status === CustomerFacingClaimRecordStatus.IN_PROGRESS) {
          const resumeToView = getResumeToView(claimRecord)

          // clear local form state unless resuming to insurance form
          resumeToView !== 'insurance-information' && clearLocalFormState()

          if (resumeToView) {
            navigate(buildPath(resumeToView))
            return
          }
        }

        // otherwise navigate to default view
        const defaultView = getDefaultView(ROUTES.BASE)
        navigate(buildPath(defaultView))
      })
      .catch(() => {
        showErrorPage()
      })
  }, [dispatch, buildPath, navigate, fetchClaimRecord, showErrorPage, state])

  return <LoadingSkeleton />
}
